import React from 'react';

interface QuizAnswers {
  salesChannels: string[];
  department: string;
  spreadsheetUsage: string;
  ecosystem: string;
  dashboardPreference: string;
  techAdoption: string;
  techTeam: string;
  speedPriority: string;
}

interface Recommendation {
  integration: { name: string; reason: string };
  warehouse: { name: string; reason: string };
  biTool: { name: string; reason: string };
}

export default function ToolQuiz() {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [answers, setAnswers] = React.useState<QuizAnswers>({
    salesChannels: [],
    department: '',
    spreadsheetUsage: '',
    ecosystem: '',
    dashboardPreference: '',
    techAdoption: '',
    techTeam: '',
    speedPriority: ''
  });
  const [showResults, setShowResults] = React.useState(false);

  const questions = [
    {
      id: 'salesChannels',
      question: 'Which platforms do you sell on?',
      type: 'multiple' as const,
      subtitle: 'Select all that apply',
      options: [
        { value: 'shopify', label: 'Shopify' },
        { value: 'amazon', label: 'Amazon' },
        { value: 'tiktok', label: 'TikTok Shop' },
        { value: 'walmart', label: 'Walmart' },
        { value: 'other', label: 'Other marketplaces' }
      ]
    },
    {
      id: 'department',
      question: 'Which department are you in?',
      type: 'single' as const,
      options: [
        { value: 'marketing', label: 'Marketing' },
        { value: 'finance', label: 'Finance or accounting' },
        { value: 'operations', label: 'Operations' },
        { value: 'executive', label: 'Executive or C-suite' },
        { value: 'other', label: 'Other' }
      ]
    },
    {
      id: 'spreadsheetUsage',
      question: 'How much do you rely on Excel or Google Sheets?',
      type: 'single' as const,
      options: [
        { value: 'heavy', label: 'Heavily. We live in spreadsheets' },
        { value: 'moderate', label: 'Moderately. We use them for some reporting' },
        { value: 'light', label: 'Lightly. We prefer other tools' },
        { value: 'moving-away', label: 'Trying to move away from spreadsheets' }
      ]
    },
    {
      id: 'ecosystem',
      question: 'Which set of tools does your team use most?',
      type: 'single' as const,
      options: [
        { value: 'microsoft', label: 'Microsoft 365, such as Outlook, Excel, and Teams' },
        { value: 'google', label: 'Google Workspace, such as Gmail, Sheets, and Drive' },
        { value: 'mix', label: 'Mix of both' },
        { value: 'neither', label: 'Neither or other tools' }
      ]
    },
    {
      id: 'dashboardPreference',
      question: 'What matters more for your dashboards?',
      type: 'single' as const,
      options: [
        { value: 'beautiful', label: 'Presentation-ready charts' },
        { value: 'functional', label: 'Fast to use. Show me the numbers' },
        { value: 'both', label: 'Both. It needs to look good and be easy to use' }
      ]
    },
    {
      id: 'techAdoption',
      question: 'How open are you to a tool your team has not used before?',
      type: 'single' as const,
      options: [
        { value: 'enthusiastic', label: 'Very open. We often try new tools' },
        { value: 'open', label: 'Open if the tool fits the work better' },
        { value: 'cautious', label: 'We prefer established tools' },
        { value: 'conservative', label: 'We prefer tools the team already knows' }
      ]
    },
    {
      id: 'techTeam',
      question: 'Do you have a technical team in house?',
      type: 'single' as const,
      options: [
        { value: 'none', label: 'No technical team' },
        { value: 'basic', label: 'Can use BI tools and write formulas' },
        { value: 'intermediate', label: 'Developers who can write SQL and scripts' },
        { value: 'advanced', label: 'Full data engineering team' }
      ]
    },
    {
      id: 'speedPriority',
      question: 'What matters most during implementation?',
      type: 'single' as const,
      options: [
        { value: 'fast', label: 'Start with the shortest setup path' },
        { value: 'balanced', label: 'Balance setup work and ongoing ownership' },
        { value: 'cost', label: 'We can do more setup and maintenance ourselves' }
      ]
    }
  ];

  const handleAnswer = (questionId: string, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question?.type === 'multiple') {
      const currentValues = answers[questionId as keyof QuizAnswers] as string[];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      setAnswers({ ...answers, [questionId]: newValues });
    } else {
      setAnswers({ ...answers, [questionId]: value });
    }
  };

  const canProceed = () => {
    const currentQuestion = questions[currentStep];
    const answer = answers[currentQuestion.id as keyof QuizAnswers];
    if (currentQuestion.type === 'multiple') {
      return (answer as string[]).length > 0;
    }
    return answer !== '';
  };

  const nextStep = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResults(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };


  const reset = () => {
    setCurrentStep(0);
    setAnswers({
        salesChannels: [],
      department: '',
      spreadsheetUsage: '',
      ecosystem: '',
      dashboardPreference: '',
      techAdoption: '',
      techTeam: '',
        speedPriority: ''
    });
    setShowResults(false);
  };

  const getRecommendations = (): Recommendation => {
    const {
      salesChannels,
      department,
      spreadsheetUsage,
      ecosystem,
      dashboardPreference,
      techAdoption,
      techTeam,
      speedPriority
    } = answers;

    // BI tool Logic
    let biTool = { name: 'Metabase', reason: 'You are open to new tools and did not name a Microsoft or Google reporting constraint.' };

    if (ecosystem === 'microsoft' || (department === 'finance' && spreadsheetUsage === 'heavy')) {
      biTool = {
        name: 'Power BI',
        reason: 'Your team uses Microsoft 365 or does finance work in Excel.'
      };
    }

    if (dashboardPreference === 'beautiful') {
      biTool = {
        name: 'Tableau',
        reason: 'You chose presentation-ready charts as the main dashboard need.'
      };
    }

    if (dashboardPreference === 'functional' || (ecosystem === 'google' && techTeam === 'advanced')) {
      biTool = {
        name: 'Looker',
        reason: 'You chose fast access to numbers, and your team can work with a modeled BI layer.'
      };
    }

    if (techAdoption === 'enthusiastic' || techAdoption === 'open') {
      biTool = {
        name: 'Metabase',
        reason: 'You said the team is open to a tool it has not used before.'
      };
    }

    // Data integration Logic
    let integration = { name: 'Airbyte', reason: 'Your answers suggest the team can test and own connector setup and changes.' };

    if (speedPriority === 'fast' && (techTeam === 'none' || techTeam === 'basic')) {
      integration = {
        name: 'Fivetran',
        reason: 'You prioritized a shorter setup path and have limited in-house technical capacity.'
      };
    }

    if (salesChannels.includes('tiktok')) {
      integration = {
        name: 'Daton',
        reason: 'You sell through TikTok Shop, so test an ecommerce-focused connector against the tables and regions you need.'
      };
    }

    if (salesChannels.includes('amazon') && !salesChannels.includes('shopify')) {
      integration = {
        name: 'Daton',
        reason: 'You sell on Amazon without Shopify, so start by testing an ecommerce-focused connector against the Seller Central tables you need.'
      };
    }

    // Data warehouse Logic
    let warehouse = { name: 'Snowflake', reason: 'You did not name a Microsoft or Google cloud preference. Test one source, one model, and one report before committing.' };

    if (ecosystem === 'microsoft') {
      warehouse = {
        name: 'Azure Synapse',
        reason: 'Your team uses Microsoft 365. Check whether Azure is also where your cloud data and technical skills live.'
      };
    }

    if (ecosystem === 'google') {
      warehouse = {
        name: 'BigQuery',
        reason: 'Your team uses Google Workspace. Check whether Google Cloud is also where your data and technical skills live.'
      };
    }

    if (ecosystem === 'mix' && speedPriority === 'fast') {
      warehouse = {
        name: 'Snowflake',
        reason: 'You prioritized a shorter setup path without naming a Microsoft or Google cloud preference.'
      };
    }

    return { integration, warehouse, biTool };
  };

  const currentQuestion = questions[currentStep];
  const recommendations = showResults ? getRecommendations() : null;

  // Email Capture Screen

  // Results Screen
  if (showResults && recommendations) {
    return (
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-2xl p-8 md:p-12">
        <div className="text-center mb-12">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">A stack to test</h3>
          <p className="text-lg text-gray-700">These picks reflect the answers you gave:</p>
        </div>

        <div className="space-y-6 mb-10">
          <div className="bg-white rounded-xl p-6 border-l-4 border-blue-500 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-xl font-bold text-gray-900">Data integration</h4>
              <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
                {recommendations.integration.name}
              </span>
            </div>
            <p className="text-gray-700 leading-loose">{recommendations.integration.reason}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border-l-4 border-indigo-500 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-xl font-bold text-gray-900">Data warehouse</h4>
              <span className="text-sm bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-semibold">
                {recommendations.warehouse.name}
              </span>
            </div>
            <p className="text-gray-700 leading-loose">{recommendations.warehouse.reason}</p>
          </div>

          <div className="bg-white rounded-xl p-6 border-l-4 border-violet-500 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-xl font-bold text-gray-900">BI tool</h4>
              <span className="text-sm bg-violet-100 text-violet-700 px-3 py-1 rounded-full font-semibold">
                {recommendations.biTool.name}
              </span>
            </div>
            <p className="text-gray-700 leading-loose">{recommendations.biTool.reason}</p>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
          <p className="text-gray-900 font-semibold mb-2">Before you choose</p>
          <p className="text-gray-700 leading-loose">
            Confirm source coverage, security, operating work, and contract terms before choosing a tool. If you want a second set of eyes, talk to Linus about your sources and reports.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="mailto:linus@greyfielddata.com?subject=Ecommerce data stack"
            className="inline-block bg-blue-600 text-white font-bold px-8 py-4 rounded-xl hover:bg-blue-700 transition text-center shadow-lg"
          >
            Get help choosing your stack
          </a>
          <button
            onClick={reset}
            className="inline-block bg-white text-gray-700 font-semibold px-8 py-4 rounded-xl hover:bg-gray-50 transition border border-gray-300"
          >
            Retake quiz
          </button>
        </div>
      </div>
    );
  }

  // Quiz Questions
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-lg">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Question {currentStep + 1} of {questions.length}</span>
          <span>{Math.round(((currentStep + 1) / questions.length) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="mb-8">
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          {currentQuestion.question}
        </h3>
        {currentQuestion.subtitle && (
          <p className="text-gray-600">{currentQuestion.subtitle}</p>
        )}
      </div>

      {/* Options */}
      <div className="space-y-3 mb-10">
        {currentQuestion.options.map((option) => {
          const isSelected = currentQuestion.type === 'multiple'
            ? (answers[currentQuestion.id as keyof QuizAnswers] as string[]).includes(option.value)
            : answers[currentQuestion.id as keyof QuizAnswers] === option.value;

          return (
            <button
              key={option.value}
              onClick={() => handleAnswer(currentQuestion.id, option.value)}
              className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-blue-600 bg-blue-50 text-blue-900 font-semibold'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                }`}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <span>{option.label}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={prevStep}
          disabled={currentStep === 0}
          className={`px-6 py-3 rounded-lg font-semibold transition ${
            currentStep === 0
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          Previous
        </button>
        <button
          onClick={nextStep}
          disabled={!canProceed()}
          className={`px-8 py-3 rounded-lg font-semibold transition ${
            canProceed()
              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {currentStep === questions.length - 1 ? 'See results' : 'Next'}
        </button>
      </div>
    </div>
  );
}
